import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { i18n } from "@/lib/i18n";
import { COOKIE_POLICY_VERSION, getCookieConsent, type CookieConsent } from "@/lib/cookieConsent";

export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [preferences, setPreferences] = useState({
    functional: true,
    analytics: true,
  });

  useEffect(() => {
    const consent = getCookieConsent();
    
    if (!consent) {
      // No consent stored, show banner
      setIsVisible(true);
      return;
    }

    // Check if policy version has changed
    if (consent.version !== COOKIE_POLICY_VERSION) {
      // Policy updated, re-prompt user
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = async () => {
    await saveConsent(true, true);
  };

  const saveConsent = async (functional: boolean, analytics: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const consent: CookieConsent = {
        version: COOKIE_POLICY_VERSION,
        essential: true,
        functional,
        analytics,
        timestamp: Date.now(),
      };

      localStorage.setItem("cookie_consent", JSON.stringify(consent));

      if (user) {
        await supabase.from("consent_log").insert({
          user_id: user.id,
          consent_type: "cookies",
          granted: functional || analytics,
        });
      }

      setIsVisible(false);
    } catch (error) {
      console.error("Error saving consent:", error);
      setIsVisible(false);
    }
  };

  const handleRejectNonEssential = async () => {
    await saveConsent(false, false);
  };

  const handleSaveCustom = async () => {
    await saveConsent(preferences.functional, preferences.analytics);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
      <Card className="mx-auto max-w-4xl p-6 shadow-lg">
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          aria-label="Close cookie banner"
        >
          <X className="h-4 w-4" />
        </button>

        {!isCustomizing ? (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">{i18n.t("cookies.title")}</h2>
            <p className="text-sm text-muted-foreground">
              {i18n.t("cookies.description")}{" "}
              <Link to="/cookie-policy" className="underline hover:text-primary">
                {i18n.t("cookies.learnMore")}
              </Link>
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleAcceptAll} className="flex-1">
                {i18n.t("cookies.accept")}
              </Button>
              <Button
                onClick={handleRejectNonEssential}
                variant="outline"
                className="flex-1"
              >
                {i18n.t("cookies.decline")}
              </Button>
              <Button
                onClick={() => setIsCustomizing(true)}
                variant="ghost"
                className="flex-1"
              >
                Customize
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Customize Cookie Preferences</h2>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1">
                  <h3 className="font-medium">Essential Cookies</h3>
                  <p className="text-sm text-muted-foreground">
                    Required for the site to function. Cannot be disabled.
                  </p>
                </div>
                <span className="text-sm font-medium text-muted-foreground">Always Active</span>
              </div>

              <div className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1">
                  <h3 className="font-medium">Functional Cookies</h3>
                  <p className="text-sm text-muted-foreground">
                    Remember your preferences and settings for a better experience.
                  </p>
                </div>
                <Switch
                  checked={preferences.functional}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, functional: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1">
                  <h3 className="font-medium">Analytics Cookies</h3>
                  <p className="text-sm text-muted-foreground">
                    Help us understand how you use our service to improve it.
                  </p>
                </div>
                <Switch
                  checked={preferences.analytics}
                  onCheckedChange={(checked) =>
                    setPreferences({ ...preferences, analytics: checked })
                  }
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={handleSaveCustom} className="flex-1">
                Save Preferences
              </Button>
              <Button
                onClick={() => setIsCustomizing(false)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
